import { Stack, Typography, type SxProps, type Theme } from '@mui/material';
import { type ReactNode } from 'react';

import useTranslate from 'hooks/useTranslate';

interface PageHeaderProps {
    title: string;
    description?: string;
    /** Sits on the title row, to the right of the title, at its own width. */
    titleEnd?: ReactNode;
    children?: ReactNode;
    /** `small` renders a compact section title for views swapped in place within a page. */
    size?: 'medium' | 'small';
    sx?: SxProps<Theme>;
}

const PageHeader = ({
    title,
    description,
    titleEnd,
    children,
    size = 'medium',
    sx,
}: PageHeaderProps) => {
    const { translate } = useTranslate();
    const isSmall = size === 'small';

    const titleNode = (
        <Typography
            variant={isSmall ? 'subtitle1' : 'h5'}
            sx={isSmall ? { fontWeight: 600 } : { fontWeight: 700, letterSpacing: '-0.02em' }}
        >
            {translate(title)}
        </Typography>
    );

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2, sm: 1.5 }}
            sx={[
                {
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    mb: isSmall ? 2 : 3,
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                {titleEnd ? (
                    <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{
                            alignItems: 'center',
                            width: 'fit-content',
                            maxWidth: '100%',
                        }}
                    >
                        {titleNode}
                        {titleEnd}
                    </Stack>
                ) : (
                    titleNode
                )}
                {description ? (
                    <Typography variant="body2" color="text.secondary">
                        {translate(description)}
                    </Typography>
                ) : null}
            </Stack>

            {children ? (
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        flexShrink: 0,
                        alignItems: 'center',
                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                        flexWrap: 'wrap',
                        '& > *': {
                            width: { xs: '100%', sm: 'auto' },
                        },
                    }}
                >
                    {children}
                </Stack>
            ) : null}
        </Stack>
    );
};

export default PageHeader;
